import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import axios from 'axios'
import { setAuthToken } from '../services/auth'
import API_URL from '../config'
import { isAuthenticated } from '../services/auth'
import React from 'react'
import { getServiceLinks, renderServiceLinksWithSeparators } from './Library/utils/serviceLinks'

const TouchDisabledStyle = () => {
  // Create a style element to fully disable animations on touch devices
  return (
    <style>
      {`
        @media (hover: none) {
          .rotation-item {
            animation: none !important;
            transform: none !important;
            transition: none !important;
          }
          .rotation-item::before {
            animation: none !important;
            box-shadow: 0 4px 8px rgba(0, 0, 0, 0.02) !important;
            transition: none !important;
          }
          .rotation-item:nth-child(odd),
          .rotation-item:nth-child(3n),
          .rotation-item:nth-child(4n) {
            animation: none !important;
            animation-delay: 0 !important;
            animation-duration: 0 !important;
          }
        }
      `}
    </style>
  )
}

const HeroPage = () => {
  const [rotationItems, setRotationItems] = useState([])
  const [loading, setLoading] = useState(false)
  const [isTouchDevice, setIsTouchDevice] = useState(false)
  const isAuth = isAuthenticated()

  useEffect(() => {
    // Detect if device is touch-capable
    const detectTouch = () => {
      setIsTouchDevice(
        'ontouchstart' in window || navigator.maxTouchPoints > 0 || navigator.msMaxTouchPoints > 0
      )
    }

    detectTouch()

    // Re-check on resize in case of device mode changes
    window.addEventListener('resize', detectTouch)
    return () => window.removeEventListener('resize', detectTouch)
  }, [])

  useEffect(() => {
    const fetchRotationItems = async () => {
      if (!isAuth) return

      try {
        setLoading(true)
        setAuthToken(localStorage.getItem('token'))
        const response = await axios.get(`${API_URL}rotation/`)
        setRotationItems(response.data)
        // Debug log to see what data we're getting
        console.log('Rotation items:', response.data)
        // Check for artists and service links in the first item
        if (response.data.length > 0) {
          console.log('First item details:', response.data[0].media_details)
        }
      } catch (error) {
        console.error('Error fetching rotation items:', error)
      } finally {
        setLoading(false)
      }
    }

    fetchRotationItems()
  }, [isAuth])

  const getMediaLink = (item) => {
    const mediaType = item.media_type
    const mediaId = item.media_id

    switch (mediaType) {
      case 'song':
        return `/song/${mediaId}`
      case 'album':
        return `/album/${mediaId}`
      case 'playlist':
        return `/playlist/${mediaId}`
      case 'mix':
        return `/mix/${mediaId}`
      case 'collage':
        return `/collage/${mediaId}`
      default:
        return '#'
    }
  }

  // Render service links using the same approach as other pages
  const renderServiceLinks = (item) => {
    console.log('Service links for item:', item.media_details?.service_links)

    if (
      !item.media_details?.service_links ||
      Object.keys(item.media_details.service_links).length === 0
    ) {
      return null
    }

    const links = getServiceLinks(item.media_details, item.media_type)

    if (!links || links.length === 0) {
      return null
    }

    return <div className='service-links center'>{renderServiceLinksWithSeparators(links)}</div>
  }

  // Render artist information
  const renderArtists = (item) => {
    if (!item.media_details?.primary_artists || !item.media_details.primary_artists.length) {
      return null
    }

    return (
      <div className='mt-1 text-sm'>
        {item.media_details.primary_artists.map((artist, index) => (
          <span key={artist.id || index}>
            {index > 0 && ', '}
            <span className='cursor-pointer hover:underline'>{artist.name}</span>
          </span>
        ))}
      </div>
    )
  }

  // Render a simplified list for touch devices
  const renderTouchFriendlyList = () => {
    return (
      <div className='mx-auto mt-12 max-w-2xl'>
        <ul className='divide-gray-200 divide-y'>
          {rotationItems.map((item) => {
            if (!item.media_details) {
              return null
            }

            return (
              <li key={item.id} className='px-4 py-4'>
                <div className='flex flex-col text-center'>
                  <span className='text-gray-500 mb-1 text-xs'>{`${item.media_type}`}</span>
                  <Link to={getMediaLink(item)}>
                    <div className='font-bold'>{item.media_details.title || 'Unknown Title'}</div>
                  </Link>
                  {renderArtists(item)}
                  {renderServiceLinks(item)}
                </div>
              </li>
            )
          })}
        </ul>
      </div>
    )
  }

  // Render original desktop rotation grid
  const renderDesktopRotationItems = () => {
    return (
      <div className='touch-action-pan-y overscroll-behavior-y-contain mx-auto mt-12 max-w-2xl overscroll-y-contain'>
        <div className='rotation-grid'>
          {rotationItems.map((item) => {
            if (!item.media_details) {
              return null
            }

            return (
              <div key={item.id} className='rotation-item'>
                <div className='hover:bg-gray-50 z-10 flex h-full flex-col items-center justify-center p-4 text-center transition-colors'>
                  <div className='flex flex-col items-center'>
                    <span className='metadata-text secondary mb-1 text-xs'>{`${item.media_type}`}</span>
                    <Link to={getMediaLink(item)}>
                      <div className='cursor-pointer font-bold hover:underline'>
                        {item.media_details.title || 'Unknown Title'}
                      </div>
                    </Link>
                  </div>
                  {renderArtists(item)}
                  {renderServiceLinks(item)}
                </div>
              </div>
            )
          })}
        </div>
      </div>
    )
  }

  const renderRotationItems = () => {
    if (!isAuth || loading) {
      return null
    }

    if (rotationItems.length === 0) {
      return null // Return nothing if rotation is empty
    }

    // Use different rendering approach based on device type
    return isTouchDevice ? renderTouchFriendlyList() : renderDesktopRotationItems()
  }

  return (
    <div className='bg-contextWhite'>
      <div className='container flex flex-col justify-center pt-12'>
        <img
          className='mx-auto h-auto w-full max-w-[400px] px-4'
          src='/lettermark.svg'
          alt='Context'
        />
        {renderRotationItems()}
      </div>
    </div>
  )
}

export default HeroPage
